import React from 'react';
import { DownloadOutlined, FullscreenOutlined } from '@ant-design/icons';
import styled from 'styled-components';

import useMultiLanguageTranslation from 'hooks/useMultiLanguageTranslation';

import { TOrbit } from '../i18n/TModules';
import { orbitTokens } from '../styles';

export interface IOrbitChartActionsProps {
  onDownload?: () => void;
  onExpand?: () => void;
  /** Held down for the length of the rasterisation, so one chart cannot be queued up twice. */
  downloading?: boolean;
}

/**
 * `figma-spec-v3.md` section 7 puts both icons inside `div.donut-row` (`5032:10878` at 42,352,
 * 409 x 137), not at the card's top right as section 4d's prose says: `download` `5032:10910` sits
 * at 399,469 and `Group 40590` `5032:10912` at 424,470, so the pair ends 14 short of the row's
 * right edge and 4 above its bottom. Three frames read identically, so the tables win.
 */
const Actions = styled.div`
  position: absolute;
  right: 14px;
  bottom: 4px;
  display: flex;
  align-items: center;

  /* Safari < 14.1 has no flex gap (conventions/components/cross-browser.md § 5). 415 to 424. */
  > * + * {
    margin-left: 9px;
  }
`;

const IconButton = styled.button<{ $size: number }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 ${({ $size }) => $size}px;
  height: ${({ $size }) => $size}px;
  padding: 0;
  border: none;
  background: transparent;
  color: ${orbitTokens.chartActionInk};
  cursor: pointer;
  line-height: ${({ $size }) => $size}px;

  .anticon {
    font-size: ${({ $size }) => $size}px;
  }

  &:hover:not(:disabled) {
    color: ${orbitTokens.action};
  }

  &:focus-visible {
    outline: 2px solid ${orbitTokens.action};
    outline-offset: 1px;
  }

  &:disabled {
    cursor: default;
    opacity: 0.45;
  }
`;

/**
 * Download and expand, on an answer's chart. Six v3 panels draw the pair, and `OrbitChart` supplies
 * both handlers; the expanded copy passes neither. The icons are antd stand-ins for the exported
 * `download` vector and the four-corner `fi-rs-expand` instance, the same arrangement as the Orbit
 * glyph under TODO(AF-13).
 */
const OrbitChartActions: React.FC<IOrbitChartActionsProps> = ({
  onDownload,
  onExpand,
  downloading = false
}) => {
  const { t } = useMultiLanguageTranslation();

  if (onDownload === undefined && onExpand === undefined) {
    return null;
  }

  return (
    <Actions data-testid="orbit-chart-actions">
      {onDownload !== undefined ? (
        <IconButton
          type="button"
          $size={16}
          aria-label={t(`${TOrbit}.analysis.download`)}
          data-testid="orbit-chart-download"
          disabled={downloading}
          onClick={onDownload}
        >
          <DownloadOutlined aria-hidden="true" />
        </IconButton>
      ) : null}
      {onExpand !== undefined ? (
        <IconButton
          type="button"
          $size={13}
          aria-label={t(`${TOrbit}.analysis.expand`)}
          data-testid="orbit-chart-expand"
          onClick={onExpand}
        >
          <FullscreenOutlined aria-hidden="true" />
        </IconButton>
      ) : null}
    </Actions>
  );
};

export default OrbitChartActions;
